<template>
  <div class="auth-wrapper">
    <div class="auth-container d-flex primary">
      <div class="logo-container">
        <div class="logo">
          <img src="@/assets/logos/cdseo-logo-white.png" />
        </div>
      </div>

      <div class="content-container accounts-container">
        <div class="accounts-inner">
          <div class="accounts-body">
            <div class="text-center logo-container-sm mb-8">
              <div class="logo">
                <img src="@/assets/logos/cdseo-logo-blue.png" />
              </div>
            </div>

            <div class="d-flex align-center justify-space-between flex-wrap mb-6">
              <h1 class="auth-title mb-0">
                Select a Workspace
              </h1>
              <div class="d-flex align-center">
                <v-btn
                  v-if="$hasRole('SUPERUSER')"
                  small
                  outlined
                  class="header-btn mr-2"
                  to="/admin"
                >
                  <v-icon left small>fa-solid fa-crown</v-icon>
                  Admin
                </v-btn>
                <v-btn
                  small
                  outlined
                  class="header-btn"
                  @click="$store.dispatch('endSession')"
                >
                  <v-icon left small>fa-solid fa-right-from-bracket</v-icon>
                  Log Out
                </v-btn>
              </div>
            </div>

            <v-row v-if="loadingAccounts" dense>
              <v-col
                v-for="n in 6"
                :key="n"
                cols="12" sm="6" lg="4"
              >
                <div class="workspace-skeleton d-flex align-center px-5">
                  <v-skeleton-loader type="avatar" class="mr-4" />
                  <v-skeleton-loader type="text" class="flex-grow-1" />
                </div>
              </v-col>
            </v-row>
            <div v-else-if="accountList.length">
              <v-row dense>
                <v-col
                  v-for="account in accountList"
                  :key="account.id"
                  cols="12" sm="6" lg="4"
                >
                  <v-list-item
                    class="workspace-item"
                    :href="account.url"
                  >
                    <v-list-item-avatar tile color="transparent" size="48" class="my-3">
                      <account-avatar :name="account.name" :size="48" />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">{{ account.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon small>fa-solid fa-chevron-right</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-col>
              </v-row>
            </div>
            <div v-else>
              <p class="text-body-1 grey--text text--darken-1">
                You do not have access to any workspaces.
              </p>
            </div>
          </div>

          <div class="accounts-footer d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex align-center status-indicator">
              <span class="status-dot"></span>
              <span class="text-caption text-uppercase font-weight-medium">All Systems Operational</span>
            </div>
            <div class="footer-links">
              <a href="https://cdseo.com/policies/privacy-policy" target="_blank" rel="noopener">Privacy</a>
              <a href="https://cdseo.com/policies/terms-of-service" target="_blank" rel="noopener">Terms</a>
              <a href="https://cdseo.com/pages/contact" target="_blank" rel="noopener">Support</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.auth-wrapper .auth-container .content-container.accounts-container {
  align-items: stretch;
  justify-content: flex-start;
}
.accounts-inner {
  margin: 0 auto;
  width: 100%;
  max-width: 1120px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
.accounts-body {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Light grey border, default (dark) text/icons. */
.header-btn.v-btn--outlined {
  border-color: #e0e0e0;
}
.theme--dark .header-btn.v-btn--outlined {
  border-color: rgba(255, 255, 255, 0.22);
}
.workspace-item {
  border: 1px solid #ccc;
  border-radius: 10px;
  min-height: 84px;
  padding: 0 20px;
}
.theme--dark .workspace-item {
  border: 1px solid #292929;
}
.workspace-item:hover {
  border-color: #0269A5;
  color: #0269A5 !important;
  cursor: pointer;
}
.workspace-item:hover .v-icon {
  color: #0269A5 !important;
}
.workspace-skeleton {
  border: 1px solid #ccc;
  border-radius: 10px;
  min-height: 84px;
}
.theme--dark .workspace-skeleton {
  border-color: #292929;
}

.accounts-footer {
  flex-shrink: 0;
  margin-top: 48px;
  padding-top: 24px;
  padding-bottom: 32px;
}
.status-indicator {
  color: #6e8ca2;
  letter-spacing: 0.06em;
}
.status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
  margin-right: 10px;
}
.footer-links a {
  color: #6e8ca2;
  margin-left: 28px;
  text-decoration: none;
  font-size: 0.9rem;
}
.footer-links a:first-child {
  margin-left: 0;
}
.footer-links a:hover {
  color: #0269A5;
}
</style>

<script>
import AccountAvatar from '@/components/AccountAvatar.vue'

export default {
  components: {
    AccountAvatar
  },
  data () {
    return {
      allAccounts: [],
      loadingAccounts: false
    }
  },
  computed: {
    // Super admins see every active workspace (same source as the admin
    // account select box); everyone else sees only their own workspaces.
    accountSource () {
      if (this.$hasRole('SUPERUSER') && this.allAccounts.length) {
        return this.allAccounts
      }
      return this.$store.getters.accounts
    },
    accountList () {
      const host = window.location.host
      return this.accountSource
        .filter(account => !!account?.subdomain)
        .map(account => {
          const url = `${window.location.protocol}//${account.subdomain}.${host.split('.').slice(1).join('.')}`
          return {
            ...account,
            url
          }
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }
  },
  created () {
    if (this.$hasRole('SUPERUSER')) {
      this.fetchAllAccounts()
    }
  },
  methods: {
    async fetchAllAccounts () {
      this.loadingAccounts = true
      try {
        const res = await this.$http.get('/api/admin/account', { params: { limit: 500 } })
        this.allAccounts = res.data?.data?.accounts || res.data?.accounts || []
      } catch (e) {
        // Fall back to the user's own workspaces on failure.
        console.error('Error fetching all workspaces:', e)
      } finally {
        this.loadingAccounts = false
      }
    }
  }
}
</script>
